import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import { MAX_GUARD, MAX_MANA } from '../engine/constants';
import { haptic, hapticSelect } from '../utils/haptics';
import '../styles/controls.css';
import { useTranslation } from 'react-i18next';

/**
 * Thumb controls, overlaid on the bottom of the stage (translucent, so the play area stays max).
 *
 *  left thumb  — a "plus" pad: ◀ ▶ held (slide between them), ▲ jump / ▼ drop on entering the
 *                upper / lower zone; diagonals combine (▲+▶ = jump right). One pointer drives the
 *                whole pad, zones are computed from the touch position relative to the pad centre.
 *  right thumb — ATTACK (press), BLOCK (hold, guard meter), MAGIC (press, mana ring, at 100 mana).
 *
 * Keyboard (desktop testing): A/← left, D/→ right, W/↑/Space jump, S/↓ drop, K/Shift block,
 * J attack, L/E magic.
 *
 * onInput({l, r, b}) fires only when the held set changes; onAct('atk'|'cast'|'jump'|'drop').
 */

export const KEY_BINDINGS = {
  KeyA: 'left',
  ArrowLeft: 'left',
  KeyD: 'right',
  ArrowRight: 'right',
  KeyW: 'jump',
  ArrowUp: 'jump',
  Space: 'jump',
  KeyS: 'drop',
  ArrowDown: 'drop',
  KeyK: 'block',
  ShiftLeft: 'block',
  ShiftRight: 'block',
  KeyJ: 'attack',
  KeyL: 'magic',
  KeyE: 'magic',
};

const KEY_FALLBACK = {
  a: 'left', d: 'right', w: 'jump', ' ': 'jump', s: 'drop', k: 'block', shift: 'block', j: 'attack', l: 'magic', e: 'magic',
  arrowleft: 'left', arrowright: 'right', arrowup: 'jump', arrowdown: 'drop',
};

/** KeyboardEvent-like {code, key} → control name or null. */
export function keyToControl(e) {
  if (!e) return null;
  if (e.code && KEY_BINDINGS[e.code]) return KEY_BINDINGS[e.code];
  const k = typeof e.key === 'string' ? e.key.toLowerCase() : '';
  return KEY_FALLBACK[k] || null;
}

export const HELD = ['left', 'right', 'block'];
export const ACT_OF = {
  attack: 'atk', magic: 'cast', jump: 'jump', drop: 'drop',
};

/** Set of active held control names → {l, r, b} (0|1). */
export function heldToInput(active) {
  return {
    l: active.has('left') ? 1 : 0,
    r: active.has('right') ? 1 : 0,
    b: active.has('block') ? 1 : 0,
  };
}

/**
 * Pad zones of a touch at (nx, ny) ∈ [-1, 1]² relative to the pad centre (y down).
 * → {h: 'left'|'right'|null, v: 'jump'|'drop'|null}
 */
export function padZone(nx, ny) {
  const h = nx < -0.3 ? 'left' : nx > 0.3 ? 'right' : null;
  let v = ny < -0.42 ? 'jump' : ny > 0.42 ? 'drop' : null;
  // a mostly-horizontal touch near the edge is a move, not a jump
  if (v && h && Math.abs(ny) < Math.abs(nx) * 0.55) v = null;
  return { h, v };
}

const NONE = { l: 0, r: 0, b: 0 };
const sameInput = (a, b) => a.l === b.l && a.r === b.r && a.b === b.b;
const DIR_ZONE = {
  left: { h: 'left', v: null },
  right: { h: 'right', v: null },
  up: { h: null, v: 'jump' },
  down: { h: null, v: 'drop' },
};

function ManaRing({ pct }) {
  const r = 44;
  const c = 2 * Math.PI * r;
  return (
    <svg className="bc-ring" viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="50" r={r} className="bc-ring__bg" />
      <circle cx="50" cy="50" r={r} className="bc-ring__fg" strokeDasharray={`${(c * pct) / 100} ${c}`} transform="rotate(-90 50 50)" />
    </svg>
  );
}

function BrawlControls({
  onInput, onAct, mana = 0, guard = MAX_GUARD, spell, disabled = false, keyboard = true, overlay = false, theme = 'arena',
}) {
  const { t } = useTranslation();
  const pointers = useRef(new Map()); // pointerId → held control (left/right/block/attack/magic)
  const padVert = useRef(new Map()); // pointerId → current vertical zone of the pad
  const keys = useRef(new Set());
  const lastInput = useRef(NONE);
  const [pressed, setPressed] = useState(() => new Set());
  const manaFull = Number(mana) >= MAX_MANA;
  const manaRef = useRef(manaFull);
  manaRef.current = manaFull;
  const cbs = useRef({ onInput, onAct });
  cbs.current = { onInput, onAct };

  const sync = useCallback(() => {
    const active = new Set([...pointers.current.values(), ...keys.current]);
    padVert.current.forEach((v) => { if (v) active.add(v); });
    setPressed((prev) => {
      if (prev.size === active.size && [...active].every((x) => prev.has(x))) return prev;
      return active;
    });
    const input = heldToInput(active);
    if (!sameInput(input, lastInput.current)) {
      lastInput.current = input;
      cbs.current.onInput?.(input);
    }
  }, []);

  const fire = useCallback((control) => {
    if (control === 'magic') {
      if (!manaRef.current) return false;
      haptic('medium');
    } else if (control === 'attack') {
      haptic('light');
    } else if (control === 'jump' || control === 'drop') {
      hapticSelect();
    }
    cbs.current.onAct?.(ACT_OF[control]);
    return true;
  }, []);

  const releaseAll = useCallback(() => {
    pointers.current.clear();
    padVert.current.clear();
    keys.current.clear();
    sync();
  }, [sync]);

  useEffect(() => {
    if (disabled) releaseAll();
  }, [disabled, releaseAll]);
  useEffect(() => {
    window.addEventListener('blur', releaseAll);
    return () => {
      window.removeEventListener('blur', releaseAll);
      if (lastInput.current.l || lastInput.current.r || lastInput.current.b) {
        lastInput.current = NONE;
        cbs.current.onInput?.(NONE);
      }
    };
  }, [releaseAll]);

  // keyboard
  useEffect(() => {
    if (!keyboard) return undefined;
    const down = (e) => {
      const c = keyToControl(e);
      if (!c) return;
      e.preventDefault();
      if (disabled) return;
      if (HELD.includes(c)) {
        if (!keys.current.has(c)) {
          keys.current.add(c);
          sync();
        }
      } else if (!e.repeat) {
        fire(c);
        keys.current.add(c);
        sync();
      }
    };
    const up = (e) => {
      const c = keyToControl(e);
      if (!c) return;
      if (keys.current.delete(c)) sync();
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [keyboard, disabled, fire, sync]);

  // ── pointer handlers ──────────────────────────────────────────────────────
  const capture = (e) => {
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (err) { /* ignore */ }
  };

  const padZoneOf = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      // no layout (tests): use the arrow under the finger
      const dir = e.target?.closest?.('[data-dir]')?.dataset?.dir;
      return DIR_ZONE[dir] || { h: null, v: null };
    }
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    return padZone(nx, ny);
  };

  const applyPad = (e, first) => {
    const { h, v } = padZoneOf(e);
    const prevH = pointers.current.get(e.pointerId) ?? null;
    const prevV = padVert.current.get(e.pointerId) ?? null;
    let changed = false;
    if (h !== prevH) {
      if (h) pointers.current.set(e.pointerId, h); else pointers.current.delete(e.pointerId);
      if (h && !first) hapticSelect();
      changed = true;
    }
    if (v !== prevV) {
      padVert.current.set(e.pointerId, v);
      if (v) fire(v); // ▲ / ▼ fire on entering the zone
      changed = true;
    }
    if (first && h) hapticSelect();
    if (changed || first) sync();
  };

  const onPadDown = (e) => {
    if (disabled) return;
    e.preventDefault();
    capture(e);
    padVert.current.set(e.pointerId, null);
    applyPad(e, true);
  };
  const onPadMove = (e) => {
    if (!padVert.current.has(e.pointerId)) return;
    applyPad(e, false);
  };
  const onUp = (e) => {
    const a = pointers.current.delete(e.pointerId);
    const b = padVert.current.delete(e.pointerId);
    if (a || b) sync();
  };

  const onButtonDown = (control) => (e) => {
    if (disabled) return;
    e.preventDefault();
    capture(e);
    if (control === 'block') {
      haptic('soft');
    } else if (!fire(control)) {
      return;
    }
    pointers.current.set(e.pointerId, control);
    sync();
  };

  const is = (c) => pressed.has(c);
  const manaPct = Math.max(0, Math.min(100, Number(mana) || 0));
  const g = Number(guard);
  const guardPct = Math.max(0, Math.min(100, ((Number.isFinite(g) ? g : MAX_GUARD) / MAX_GUARD) * 100));
  const pointerProps = {
    onPointerUp: onUp,
    onPointerCancel: onUp,
    onLostPointerCapture: onUp,
    onContextMenu: (e) => e.preventDefault(),
  };

  const arrow = (dir, control, label, d) => (
    <div
      data-dir={dir}
      className={`bc-pad__btn bc-pad__btn--${dir}${is(control) ? ' is-pressed' : ''}`}
      aria-label={label}
      role="button"
    >
      <svg viewBox="0 0 24 24"><path d={d} /></svg>
    </div>
  );

  return (
    <div className={`bc bc--${theme}${overlay ? ' bc--overlay' : ''}${disabled ? ' bc--disabled' : ''}`}>
      <div
        className="bc-pad"
        role="group"
        aria-label={t('brawl.control.move', 'Move')}
        onPointerDown={onPadDown}
        onPointerMove={onPadMove}
        onPointerLeave={(e) => { if (e.pointerType === 'mouse') onUp(e); }}
        {...pointerProps}
      >
        <div className="bc-pad__ring" />
        {arrow('up', 'jump', t('brawl.control.jump', 'Jump'), 'M4 15.5 12 7l8 8.5')}
        {arrow('left', 'left', t('brawl.control.left', 'Left'), 'M15.5 4 7 12l8.5 8')}
        {arrow('right', 'right', t('brawl.control.right', 'Right'), 'M8.5 4 17 12l-8.5 8')}
        {arrow('down', 'drop', t('brawl.control.drop', 'Drop'), 'M4 8.5 12 17l8-8.5')}
      </div>

      <div className="bc-actions">
        <button
          type="button"
          className={`bc-btn bc-btn--magic${is('magic') ? ' is-pressed' : ''}${manaFull ? ' is-ready' : ''}`}
          onPointerDown={onButtonDown('magic')}
          {...pointerProps}
          aria-label={t('brawl.control.magic', 'Magic')}
          aria-disabled={!manaFull}
        >
          <ManaRing pct={manaPct} />
          <span className="bc-btn__icon">{spell?.icon || '✨'}</span>
          <span className="bc-btn__label">{manaFull ? t('brawl.control.magic_label', 'MAGIC') : `${Math.floor(manaPct)}%`}</span>
        </button>
        <button
          type="button"
          className={`bc-btn bc-btn--block${is('block') ? ' is-pressed' : ''}${guardPct < 30 ? ' is-low' : ''}`}
          onPointerDown={onButtonDown('block')}
          {...pointerProps}
          aria-label={t('brawl.control.block', 'Block')}
          style={{ '--guard': `${guardPct}%` }}
        >
          <span className="bc-btn__guard" />
          <span className="bc-btn__icon">🛡</span>
          <span className="bc-btn__label">{t('brawl.control.block_label', 'BLOCK')}</span>
        </button>
        <button
          type="button"
          className={`bc-btn bc-btn--attack${is('attack') ? ' is-pressed' : ''}`}
          onPointerDown={onButtonDown('attack')}
          {...pointerProps}
          aria-label={t('brawl.control.attack', 'Attack')}
        >
          <span className="bc-btn__icon">👊</span>
          <span className="bc-btn__label">{t('brawl.control.attack_label', 'ATTACK')}</span>
        </button>
      </div>
    </div>
  );
}

export default memo(BrawlControls);
